import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    DragStartEvent,
    DragOverlay,
    defaultDropAnimationSideEffects
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy
} from '@dnd-kit/sortable';

import { ScriptBlock, ScriptBlockData } from './ScriptBlock';
import {ScriptEditingToolbar} from './ScriptEditingToolbar';
import {ScriptDraftPaper} from './ScriptDraftPaper';
import { BlockType } from './SlashMenu';
import { importFDXDocument, projectFDXToRichBlocks, exportEditedFDX,readFDXRevisions } from '../../lib/fdxConverter';
import { replaceBlockText, observedTextEdit,runsFor, withRuns, toggleTextStyle,mergeRichParagraphs,deletionMarksFor, type TextStyle,type ScriptTextEdit } from '../../lib/scriptRichText';
import { exportToPDF } from '../../lib/pdfGenerator';
import { ProjectService } from '../../lib/projectService';
import type { ProjectVersion } from '../../lib/projectService';
import { ScriptSaveSession, validateScriptBlocks, validateScriptDocument } from '../../lib/scriptPersistence';
import type { SaveStatus, ScriptDocumentData, ScriptSnapshot } from '../../lib/scriptPersistence';
import { ScriptRecoveryJournal, validateSnapshot } from '../../lib/scriptRecovery';
import type { RecoveryDraft } from '../../lib/scriptRecovery';
import { getCharactersByProjectId } from '../../lib/character2dService';
import {buildCompletionLists,readFDXCompletionSource} from '../../lib/scriptCompletion';
import { TitlePageData } from './TitlePage';
import { layoutScreenplay } from '../../lib/scriptLayout';
import {readFDXFormatSource} from '../../lib/scriptFormat';
import { SceneNavigator } from './SceneNavigator';
import { ScriptUndoHistory, type ScriptEditIntent } from '../../lib/scriptUndo';
import {withDual, replaceDualText, type ScriptDualDialogue} from '../../lib/scriptDualDialogue';
import { ScriptStats } from './ScriptStats';
import { FindReplace } from './FindReplace';
import { TitlePagePreview } from './TitlePagePreview';
import { Modal, InputModal, ConfirmModal } from '../ui/modal';
import { PlusIcon, DownloadIcon, PrinterIcon, GearSixIcon, FileArrowUpIcon, FileArrowDownIcon, ArrowCounterClockwiseIcon, ArrowClockwiseIcon, ClockCounterClockwiseIcon, FloppyDiskIcon, EyeIcon, EyeClosedIcon, CheckSquareIcon, ListBulletsIcon, ChartBarIcon, FilmSlateIcon, ClockIcon, MagnifyingGlassIcon, HashIcon } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { changeElement, insertScriptBlock, speakerName, tabTransition } from './scriptEditing';
import type { BlockInsertion, ScriptCaretRequest } from './scriptEditing';
import {createRevisions,markRevision,validateRevisionReferences,collatedRevisionRanges,revisionParagraphs,clearDeletionMarks,removeRevisionBlocks,type ScriptRevisions} from '../../lib/scriptRevisions';
import {RevisionSettings} from './RevisionSettings';
import { blockStartPages } from '../../lib/scriptOutline';
import { SceneCards } from './SceneCards';

export interface ScriptEditorHandle {
    exportPDF: () => void;
    toggleHistory: () => void;
    saveVersion: () => void;
    toggleSceneNavigator: () => void;
    toggleStats: () => void;
    toggleFindReplace: () => void;
    toggleSceneNumbers: () => void;
    toggleTitlePagePreview: () => void;
    toggleIndexCards: () => void;
}

interface ScriptEditorProps {
    projectId?: string;
    accountId?: string;
    onSaveStatusChange?: (status: 'saved' | 'saving' | 'error') => void;
    onViewModeChange?: (mode: 'screenplay' | 'cards') => void;
    readOnly?: boolean;
}

export const ScriptEditor = React.forwardRef<ScriptEditorHandle, ScriptEditorProps>(({ projectId, accountId, onSaveStatusChange, onViewModeChange, readOnly: permissionReadOnly = false }, ref) => {
    // Initial seed data
    const [blocks, setBlocks] = useState<ScriptBlockData[]>([]);
    const [projectCharacters, setProjectCharacters] = useState<any[]>([]); // Store project characters for linking
    const [isLoading, setIsLoading] = useState(true);
    const [loadedProjectId, setLoadedProjectId] = useState<string | undefined | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [loadAttempt, setLoadAttempt] = useState(0);
    const [saveStatus, setSaveStatus] = useState<SaveStatus>('saving');
    const [saveError, setSaveError] = useState<string | null>(null);
    const saveSession = useRef<ScriptSaveSession | null>(null);
    const [recoveryDrafts, setRecoveryDrafts] = useState<RecoveryDraft[]>([]);
    const [recoveryError, setRecoveryError] = useState<string | null>(null);
    const [historyError, setHistoryError] = useState<string | null>(null);
    const [historyBusy, setHistoryBusy] = useState(false);
    const [cloudRevision, setCloudRevision] = useState(0);
    const readOnly = permissionReadOnly || isLoading || !!loadError || loadedProjectId !== projectId || recoveryDrafts.length > 0 || historyBusy;

    const [activeId, setActiveId] = useState<string | null>(null);
    const [caretRequest, setCaretRequest] = useState<ScriptCaretRequest>();
    const [editingToolbarTarget,setEditingToolbarTarget]=useState<HTMLDivElement|null>(null);
    const [dragActiveId, setDragActiveId] = useState<string | null>(null);
    const [projectTitle, setProjectTitle] = useState('');
    const [projectType, setProjectType] = useState<string>('Screenplay');

    // Multi-select State
    const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
    const [selectedBlockIds, setSelectedBlockIds] = useState<Set<string>>(new Set());
    const containerRef = useRef<HTMLDivElement>(null);

    // Script Properties State
    const [showProperties, setShowProperties] = useState(true);
    const [showDocumentDetails,setShowDocumentDetails]=useState(false);
    const [documentData, setDocumentData] = useState<ScriptDocumentData>(() => validateScriptDocument(null));
    const [showRevisions,setShowRevisions]=useState(false);

    // Title Page Data
    const [titlePageData, setTitlePageData] = useState<TitlePageData>({
        title: 'UNTITLED SCREENPLAY',
        author: 'Unknown Author',
        contact: ''
    });
    const [showTitlePageInfo, setShowTitlePageInfo] = useState(false);

    // Versioning State
    const [showHistory, setShowHistory] = useState(false);
    const [versions, setVersions] = useState<ProjectVersion[]>([]);
    const [loadingVersions, setLoadingVersions] = useState(false);

    // Panel State
    const [showSceneNavigator, setShowSceneNavigator] = useState(false);
    const [showStats, setShowStats] = useState(false);

    // Views: the screenplay page, or one index card per scene
    const [viewMode, setViewMode] = useState<'screenplay' | 'cards'>('screenplay');

    const onViewModeChangeRef = useRef(onViewModeChange);
    onViewModeChangeRef.current = onViewModeChange;
    useEffect(() => { onViewModeChangeRef.current?.(viewMode); }, [viewMode]);

    // Modal State
    const [showVersionModal, setShowVersionModal] = useState(false);
    const [versionToRestore, setVersionToRestore] = useState<ProjectVersion | null>(null);
    const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);

    // Find & Replace State
    const [showFindReplace, setShowFindReplace] = useState(false);

    // Scene Numbers State
    const [showSceneNumbers, setShowSceneNumbers] = useState(false);

    // Block Notes State
    const [blockNotes, setBlockNotes] = useState<Record<string, string>>({});
    const [importOpen, setImportOpen] = useState(false);
    const [importError, setImportError] = useState<string | null>(null);
    const [importReview, setImportReview] = useState<{fileName: string; xml: string; blocks: ScriptBlockData[]; warnings: string[];revisions?:ScriptRevisions} | null>(null);
    const importGeneration = useRef(0);
    const undoHistory = useRef<ScriptUndoHistory | null>(null);
    const editIntent = useRef<ScriptEditIntent | undefined>(undefined);
    const historyJumpPending = useRef(false);
    const [undoState, setUndoState] = useState({canUndo:false, canRedo:false, undoLabel:undefined as string | undefined, redoLabel:undefined as string | undefined});
    const markEdit = (label: string, group?: string, focusId?: string) => { editIntent.current = {label, group, focusId}; };
    const refreshUndoState = () => {
        const history = undoHistory.current;
        setUndoState({canUndo:history?.canUndo ?? false, canRedo:history?.canRedo ?? false,
            undoLabel:history?.undoLabel, redoLabel:history?.redoLabel});
    };
    const currentSnapshot = useMemo<ScriptSnapshot>(() => ({ blocks, titlePage: titlePageData,
        document: { ...documentData, blockNotes, settings: { ...documentData.settings,
            showSceneNumbers, showScriptProperties: showProperties } } }),
        [blocks, titlePageData, documentData, blockNotes, showSceneNumbers, showProperties]);
    const applySnapshot = useCallback((snapshot: ScriptSnapshot) => {
        const document = validateScriptDocument(snapshot.document);
        validateRevisionReferences(snapshot.blocks,document.revisions);
        setBlocks(snapshot.blocks);
        setTitlePageData(snapshot.titlePage);
        setProjectTitle(snapshot.titlePage.title);
        setDocumentData(document);
        setBlockNotes(document.blockNotes);
        setShowSceneNumbers(document.settings.showSceneNumbers);
        setShowProperties(document.settings.showScriptProperties);
    }, []);
    const toggleSceneNumbers = () => { if (!readOnly) {markEdit('Change scene number display'); setShowSceneNumbers(prev => !prev);} };

    const travelHistory = (direction: 'undo' | 'redo') => {
        const history = undoHistory.current;
        if (readOnly || !history || historyJumpPending.current) return;
        // Capture any committed render whose observation effect has not run yet.
        history.record(currentSnapshot, editIntent.current);
        editIntent.current = undefined;
        const point = direction === 'undo' ? history.undo() : history.redo();
        if (!point) { refreshUndoState(); return; }
        historyJumpPending.current = true;
        applySnapshot(point.snapshot);
        setCaretRequest(undefined);
        setActiveId(point.snapshot.blocks.find(block => block.id === (point.focusId ?? activeId))?.id ?? point.snapshot.blocks[0]?.id ?? null);
        setSelectedBlockIds(new Set()); setIsMultiSelectMode(false);
        refreshUndoState();
    };
    // The timeline observes the same complete immutable snapshots as autosave.
    // Hydration is seeded explicitly; undo/redo moves the cursor before applying
    // state, so observing that render cannot create an edit or discard redo.
    useEffect(() => {
        if (isLoading || loadError || loadedProjectId !== projectId || !undoHistory.current) return;
        if (!readOnly) undoHistory.current.record(currentSnapshot, editIntent.current);
        editIntent.current = undefined; historyJumpPending.current = false;
        refreshUndoState();
    }, [currentSnapshot, isLoading, loadError, loadedProjectId, projectId, readOnly]);

    useEffect(() => {
        const ownsInput = (target: EventTarget | null) => target instanceof Element
            && containerRef.current?.contains(target) && !target.closest('[data-story-native-undo], [role="dialog"]');
        const keyboard = (event: KeyboardEvent) => {
            if (ownsInput(event.target) && ['ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End','PageUp','PageDown','Tab'].includes(event.key)) undoHistory.current?.endGroup();
            if (event.defaultPrevented || event.isComposing || !(event.ctrlKey || event.metaKey) || event.altKey || !ownsInput(event.target)) return;
            const key = event.key.toLowerCase();
            if (key !== 'z' && key !== 'y') return;
            event.preventDefault();
            travelHistory(key === 'y' || event.shiftKey ? 'redo' : 'undo');
        };
        const beforeInput = (event: Event) => {
            const input = event as InputEvent;
            if (!['historyUndo','historyRedo'].includes(input.inputType) || !ownsInput(event.target)) return;
            event.preventDefault(); travelHistory(input.inputType === 'historyRedo' ? 'redo' : 'undo');
        };
        document.addEventListener('keydown', keyboard, true);
        document.addEventListener('beforeinput', beforeInput, true);
        return () => {document.removeEventListener('keydown', keyboard, true); document.removeEventListener('beforeinput', beforeInput, true);};
    });

    // Title Page Preview State
    const [showTitlePagePreview, setShowTitlePagePreview] = useState(false);
    const [exportError, setExportError] = useState<string | null>(null);

    // Page count (memoized)
    const scriptFormat=useMemo(()=>readFDXFormatSource(documentData.sourceFDX?.xml),[documentData.sourceFDX?.xml]);
    const printLayout = useMemo(() => layoutScreenplay(blocks,scriptFormat), [blocks,scriptFormat]);
    const revisionRanges=useMemo(()=>collatedRevisionRanges(printLayout,blocks,documentData.revisions),[printLayout,blocks,documentData.revisions]);
    const pageCount = printLayout.pages.length;
    const handleExportPDF = useCallback(() => {
        try { exportToPDF(blocks, titlePageData, printLayout,documentData.revisions); }
        catch (error) { setExportError(error instanceof Error ? error.message : 'PDF export failed.'); }
    }, [blocks, titlePageData, printLayout,documentData.revisions]);
    const sceneCount = useMemo(() => blocks.filter(b => b.type === 'scene-heading').length, [blocks]);
    const startPages = useMemo(() => blockStartPages(printLayout, scriptFormat), [printLayout, scriptFormat]);

    // Scene number mapping (memoized)
    const sceneNumberMap = useMemo(() => {
        const map: Record<string, number | string> = {};
        let sceneNum = 0;
        blocks.forEach(block => {
            if (block.type === 'scene-heading') {
                sceneNum++;
                map[block.id] = block.richText?.paragraphAttributes.Number || sceneNum;
            }
        });
        return map;
    }, [blocks]);

    // Expose actions to parent
    React.useImperativeHandle(ref, () => ({
        exportPDF: handleExportPDF,
        toggleHistory: () => setShowHistory(prev => !prev),
        saveVersion: () => setShowVersionModal(true),
        toggleSceneNavigator: () => setShowSceneNavigator(prev => !prev),
        toggleStats: () => setShowStats(prev => !prev),
        toggleFindReplace: () => setShowFindReplace(prev => !prev),
        toggleSceneNumbers,
        toggleTitlePagePreview: () => setShowTitlePagePreview(prev => !prev),
        toggleIndexCards: () => setViewMode(prev => prev === 'cards' ? 'screenplay' : 'cards')
    }));

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Cmd/Ctrl + F for Find & Replace
            if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
                e.preventDefault();
                setShowFindReplace(true);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    // Load Data
    useEffect(() => {
        let current = true;
        saveSession.current?.close();
        saveSession.current = null;
        undoHistory.current = null; editIntent.current = undefined; historyJumpPending.current = false;
        refreshUndoState();
        setLoadedProjectId(null);
        setLoadError(null);
        setSaveError(null);
        setSaveStatus('saving');
        setRecoveryDrafts([]);
        setRecoveryError(null);
        setHistoryError(null);
        setHistoryBusy(false);
        setCloudRevision(0);
        setBlocks([]);
        setProjectCharacters([]);
        setProjectTitle('');
        setTitlePageData({ title: '', author: 'Unknown Author', contact: '' });
        setBlockNotes({});
        setDocumentData(validateScriptDocument(null));
        setShowSceneNumbers(false);
        setShowProperties(true);
        setImportOpen(false);
        setImportReview(null);
        setImportError(null);
        setFDXExport(null);
        importGeneration.current++;
        setActiveId(null);
        setDragActiveId(null);
        setSelectedBlockIds(new Set());
        setVersions([]);
        setVersionToRestore(null);
        setShowRestoreConfirm(false);
        const load = async () => {
            setIsLoading(true);
            if (projectId) {
                try {
                    const project = await ProjectService.loadScriptDocument(projectId);
                    if (!current) return;
                    const loadedBlocks = validateScriptBlocks(project.script_content);
                    if (project.title_page != null && (typeof project.title_page !== 'object' || Array.isArray(project.title_page))) {
                        throw new Error('The stored title page could not be read.');
                    }
                    const titlePage = { title: project.title, author: 'Unknown Author', contact: '', ...project.title_page };
                    if (typeof titlePage.title !== 'string' || typeof titlePage.author !== 'string' || typeof titlePage.contact !== 'string') {
                        throw new Error('The stored title page contains invalid fields.');
                    }
                    if (!Number.isSafeInteger(project.script_revision) || project.script_revision! < 0) {
                        throw new Error('The screenplay revision could not be read.');
                    }
                    const document = validateScriptDocument(project.script_document);
                    if (project.script_document == null && typeof project.metadata?.show_script_properties === 'boolean') {
                        document.settings.showScriptProperties = project.metadata.show_script_properties;
                    }
                    const loadedSnapshot = validateSnapshot({ blocks: loadedBlocks, titlePage, document });
                    let journal: ScriptRecoveryJournal | undefined;
                    if (accountId) {
                        try {
                            journal = new ScriptRecoveryJournal(window.localStorage, accountId, projectId, crypto.randomUUID());
                            const recovery = journal.list();
                            const snapshot = JSON.stringify(loadedSnapshot);
                            setRecoveryDrafts(recovery.drafts.filter(draft => JSON.stringify({ ...draft.snapshot,
                                document: validateScriptDocument(draft.snapshot.document) }) !== snapshot));
                            if (recovery.unreadable) setRecoveryError('Some local recovery entries could not be read. They have been retained.');
                        } catch { setRecoveryError('Local recovery storage is unavailable in this browser.'); }
                    }
                    setCloudRevision(project.script_revision!);
                    saveSession.current = new ScriptSaveSession(projectId,
                        loadedSnapshot, ProjectService.saveScriptDocument,
                        (status, error) => { setSaveStatus(status); setSaveError(error || null); }, 1000,
                        { revision: project.script_revision!, journal,
                            onRecoveryError: message => { if (current) setRecoveryError(message || null); } });
                    undoHistory.current = new ScriptUndoHistory(loadedSnapshot);
                    applySnapshot(loadedSnapshot);
                    setProjectType(project.project_type || 'Screenplay');
                    setLoadedProjectId(projectId);
                    setSaveStatus('saved');
                    // Optional character suggestions must not delay script hydration.
                    void getCharactersByProjectId(projectId).then(characters => {
                        if (current) setProjectCharacters(characters);
                    }).catch(() => { /* The screenplay can be edited without suggestions. */ });
                } catch (e) {
                    if (!current) return;
                    setLoadError('Could not load this screenplay. Your stored document has not been changed.');
                    setSaveStatus('error');
                }
            } else {
                // Draft Mode (Legacy/Fallback)
                const draft: ScriptSnapshot = {blocks:[
                    { id: '1', type: 'scene-heading', content: 'INT. UNTITLED - DAY' },
                    { id: '2', type: 'action', content: 'Please select a project to save your work.' },
                ], titlePage:{title:'Screenplay Draft 1',author:'Unknown Author',contact:''}, document:validateScriptDocument(null)};
                undoHistory.current = new ScriptUndoHistory(draft); applySnapshot(draft);
                setLoadedProjectId(undefined);
                setSaveStatus('saved');
            }
            if (current) setIsLoading(false);
        };
        void load();
        return () => {
            current = false;
            saveSession.current?.close();
            saveSession.current = null;
        };
    }, [projectId, accountId, loadAttempt, applySnapshot]);

    const importedCompletions = React.useMemo(() => readFDXCompletionSource(documentData.sourceFDX?.xml),[documentData.sourceFDX?.xml]);
    const suggestions = React.useMemo(() => buildCompletionLists(blocks.filter(block=>block.id!==activeId),projectCharacters,importedCompletions),[blocks,activeId,projectCharacters,importedCompletions]);

    // Notify parent of save status
    useEffect(() => {
        onSaveStatusChange?.(saveStatus);
    }, [saveStatus, onSaveStatusChange]);

    // The whole document shares one serialized, project-bound save queue.
    useEffect(() => {
        const session = saveSession.current;
        if (!session || session.projectId !== projectId || loadedProjectId !== projectId) return;
        session.setWritable(!readOnly);
        if (!readOnly) session.schedule(currentSnapshot);
    }, [currentSnapshot, projectId, loadedProjectId, readOnly]);

    useEffect(() => {
        const warnUnsaved = (event: BeforeUnloadEvent) => {
            if (!saveSession.current?.pending) return;
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', warnUnsaved);
        return () => window.removeEventListener('beforeunload', warnUnsaved);
    }, []);

    // Toggle block selection in multi-select mode
    const toggleBlockSelection = (id: string) => {
        setSelectedBlockIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    };

    // Exit multi-select mode
    const exitMultiSelectMode = () => {
        setIsMultiSelectMode(false);
        setSelectedBlockIds(new Set());
    };




    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5 // drag after 5px (prevents accidental drags when clicking to edit)
            }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragStart = (event: DragStartEvent) => {
        if (readOnly) return;
        const id = event.active.id as string;

        // In multi-select mode, preserve the selection
        // If dragging an item that is NOT in the selection, add it to selection (don't clear)
        // If dragging an item that IS in the selection, keep the selection (drag group)
        if (isMultiSelectMode) {
            if (!selectedBlockIds.has(id)) {
                setSelectedBlockIds(prev => new Set([...prev, id]));
            }
        } else {
            // Normal mode: If dragging an item that is NOT in the selection, clear selection and select only that item
            if (!selectedBlockIds.has(id)) {
                setSelectedBlockIds(new Set([id]));
            }
        }

        setDragActiveId(id);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        if (readOnly) return;
        markEdit('Move screenplay blocks');
        const { active, over } = event;

        if (over && active.id !== over.id) {
            setBlocks((items) => {
                const oldIndex = items.findIndex((item) => item.id === active.id);
                const newIndex = items.findIndex((item) => item.id === over.id);

                // Multi-drag Logic
                if (selectedBlockIds.size > 1 && selectedBlockIds.has(active.id as string)) {
                    // We are moving the group
                    // 1. Get all selected items ordered by their current position
                    const orderedSelected = items.filter(b => selectedBlockIds.has(b.id));
                    // 2. Remove them from the list
                    const remaining = items.filter(b => !selectedBlockIds.has(b.id));
                    // 3. Find insertion index in the *remaining* list
                    // The 'over' item is in the remaining list (usually). 
                    // Wait, 'over' might be one of the selected items if it's a huge group? 
                    // Sortable usually handles collisions with non-dragged items.
                    // The 'over' id is what we dropped ON TOP OF.

                    let insertIndex = remaining.findIndex(b => b.id === over.id);
                    if (insertIndex === -1) {
                        // Fallback
                        return arrayMove(items, oldIndex, newIndex);
                    }

                    // If dragging downwards, we insert AFTER the target? 
                    // dnd-kit usually swaps.
                    // Logic: If newIndex > oldIndex, we are moving down.
                    // We want the group to end up at the position of 'over'.

                    const isMovingDown = newIndex > oldIndex;
                    if (isMovingDown) {
                        insertIndex++;
                    }

                    const newBlocks = [...remaining];
                    newBlocks.splice(insertIndex, 0, ...orderedSelected);
                    return newBlocks;
                }

                return arrayMove(items, oldIndex, newIndex);
            });
        }

        // Multi-select improvement: exit multi-select mode automatically after group drag & drop
        if (isMultiSelectMode) {
            exitMultiSelectMode();
        }

        setDragActiveId(null);
    };

    const updateBlock = (id: string, content: string, explicitCharacterId?: string,edit?:ScriptTextEdit) => {
        if (readOnly) return;
        markEdit('Edit text', explicitCharacterId ? undefined : `text:${id}`, id);
        setBlocks(previous => previous.map(block => {
            if (block.id !== id) return block;
            const characterId = block.type === 'character'
                ? explicitCharacterId || projectCharacters.find(character =>
                    speakerName(character.name) === speakerName(content))?.id
                : undefined;
            return { ...replaceBlockText(block, content,documentData.revisions?.mode?documentData.revisions.activeSet:undefined,documentData.revisions?.trackDeletions,edit), characterId };
        }));
    };

    const formatBlock = (id: string, start: number, end: number, style: TextStyle) => {
        if (readOnly) return;
        markEdit(`Change ${style.toLowerCase()} formatting`, undefined, id);
        setBlocks(previous => previous.map(block => block.id === id ? toggleTextStyle(block, start, end, style) : block));
    };
    const updateParagraphAttributes = (id: string, values: Record<string, string>) => {
        if (readOnly) return;
        markEdit('Change scene identity', undefined, id);
        setBlocks(previous => previous.map(block => block.id === id ? withRuns(block, runsFor(block), values) : block));
    };

    const updateType = (id: string, type: BlockType) => {
        if (readOnly) return;
        const target=blocks.find(block=>block.id===id);
        if(target&&target.type!==type&&(type==='dual-dialogue'||target.type==='dual-dialogue')&&revisionParagraphs([target]).some(p=>deletionMarksFor(p).length)){setExportError('Clear this paragraph’s deletion marks before converting dual dialogue. Save a snapshot to retain the marked draft.');return;}
        if(type==='dual-dialogue' && target?.alternates || target?.dualDialogue && type!=='dual-dialogue'
            && [...target.dualDialogue.left,...target.dualDialogue.right].some(p=>p.alternates)) {
            setExportError('This conversion would discard dialogue alternatives. Keep the current paragraph structure or remove its alternatives first.'); return;
        }
        markEdit('Change screenplay element', undefined, id);
        setBlocks(previous => previous.map(block =>
            block.id === id ? changeElement(block, type) : block));
    };

    const insertBlock = (afterId: string, request: BlockInsertion) => {
        if (readOnly) return;
        markEdit(request.kind === 'split' ? 'Split paragraph' : request.kind === 'duplicate' ? 'Duplicate paragraph' : 'Insert paragraph', undefined, afterId);
        const id = crypto.randomUUID();
        setBlocks(previous => insertScriptBlock(previous, afterId, request, id,documentData.revisions?.mode?documentData.revisions.activeSet:undefined,documentData.revisions?.trackDeletions));
        setActiveId(id);
        const start = request.kind === 'insert' && request.type === 'parenthetical' ? 1 : 0;
        setCaretRequest({start, end:start});
    };

    const updateAlternate = (paragraph:ScriptBlockData) => {
        if(readOnly)return;
        markEdit('Change dialogue alternative',undefined,paragraph.id);
        setBlocks(previous=>previous.map(block=>block.id===paragraph.id ? paragraph : block));
    };
    const updateRevisionMark=(id:string,start:number,end:number,clear:boolean)=>{
        if(readOnly||!documentData.revisions)return;
        markEdit(clear?'Clear revision mark':'Mark revision',undefined,id);
        setBlocks(previous=>previous.map(block=>block.id===id?markRevision(block,start,end,clear?undefined:documentData.revisions!.activeSet):block));
    };
    const openRevisionSettings=()=>{
        if(readOnly)return;
        try{if(!documentData.revisions){const revisions=(documentData.sourceFDX?readFDXRevisions(documentData.sourceFDX.xml):undefined)??createRevisions();validateRevisionReferences(blocks,revisions);markEdit('Create revision settings');setDocumentData(previous=>({...previous,revisions}));}setShowRevisions(true);}
        catch(error){setExportError(error instanceof Error?error.message:'Revision settings could not be read.');}
    };

    const addBlock = (afterId: string, splitContent?: string, currentContent?: string) => {
        insertBlock(afterId, splitContent === undefined
            ? { kind: 'insert' }
            : { kind: 'split', before: currentContent ?? '', after: splitContent });
    };

    const deleteBlock = (id: string) => {
        if (readOnly) return;
        markEdit('Delete screenplay blocks', undefined, id);
        // Multi-delete support?
        if (selectedBlockIds.has(id) && selectedBlockIds.size > 1) {
            // Delete all selected
            if (blocks.length <= selectedBlockIds.size) return; // Prevent deleting everything logic if strict? Notion allows deleting all.
            setBlocks(previous => removeRevisionBlocks(previous,selectedBlockIds,documentData.revisions));
            // Focus previous of the first deleted?
            // Simplified: Focus first block remaining
            return;
        }

        if (blocks.length <= 1) return; // Don't delete last block
        const index = blocks.findIndex(b => b.id === id);
        setBlocks(previous => removeRevisionBlocks(previous,new Set([id]),documentData.revisions));
        // Focus previous
        if (index > 0) {
            setActiveId(blocks[index - 1].id);
        }
    };

    const moveSelection = (id: string, direction: 'up' | 'down') => {
        const index = blocks.findIndex(b => b.id === id);
        if (direction === 'up' && index > 0) {
            setActiveId(blocks[index - 1].id);
        } else if (direction === 'down' && index < blocks.length - 1) {
            setActiveId(blocks[index + 1].id);
        }
    };

    const handleTab = (id: string) => {
        if (readOnly) return;
        const block = blocks.find(b => b.id === id);
        if (!block) return;
        const next = tabTransition(block);
        if (!next) return;
        if (next.insert) insertBlock(id, {kind:'insert', type:next.type});
        else {
            if (block.type === 'parenthetical' && block.content.trim() === '()') updateBlock(id, '');
            updateType(id, next.type);
            const start = next.type === 'parenthetical' ? 1 : 0;
            setCaretRequest({start, end:start});
        }
    };

    const handleBackspace = (id: string) => {
        if (readOnly) return;
        markEdit('Merge paragraphs');
        const index = blocks.findIndex(block => block.id === id);
        if (index <= 0 || blocks[index].type === 'dual-dialogue'
            || blocks[index - 1].type === 'dual-dialogue') return;
        const previousId = blocks[index - 1].id;
        setBlocks(previous => {
            const currentIndex = previous.findIndex(block => block.id === id);
            if (currentIndex <= 0) return previous;
            const current = previous[currentIndex];
            const before = previous[currentIndex - 1];
            if (current.type === 'dual-dialogue' || before.type === 'dual-dialogue' || current.alternates || before.alternates) return previous;
            const result = [...previous];
            result[currentIndex - 1] = before.richText || current.richText
                ? mergeRichParagraphs(before,current)
                : {...before, content: before.content + (before.content && current.content ? ' ' : '') + current.content};
            result.splice(currentIndex, 1);
            return result;
        });
        setActiveId(previousId);
    };

    const toggleProperties = () => {
        if (!readOnly) {markEdit('Change script properties display'); setShowProperties(prev => !prev);}
    };

    // --- Versioning Handlers ---
    const handleFetchVersions = async () => {
        if (!projectId || loadedProjectId !== projectId) return;
        const session = saveSession.current;
        setLoadingVersions(true);
        setHistoryError(null);
        try {
            const list = await ProjectService.getVersions(projectId);
            if (saveSession.current === session) setVersions(list);
        } catch {
            if (saveSession.current === session) setHistoryError('Version history could not be loaded. Please retry.');
        } finally {
            if (saveSession.current === session) setLoadingVersions(false);
        }
    };

    const handleCreateVersion = async (label: string) => {
        if (!projectId || readOnly) return;
        setShowVersionModal(false);
        const session = saveSession.current;
        setHistoryBusy(true);
        setHistoryError(null);
        try {
            await ProjectService.createVersion(projectId, currentSnapshot, label || 'Snapshot');
            if (saveSession.current === session) void handleFetchVersions();
        } catch (e) {
            if (saveSession.current === session) setHistoryError('The snapshot was not saved. Your screenplay is unchanged; please retry.');
        } finally {
            if (saveSession.current === session) setHistoryBusy(false);
        }
    };

    const handleRestoreVersion = async () => {
        if (readOnly || !projectId || !versionToRestore || saveSession.current?.conflicted) return;
        const session = saveSession.current;
        const target = versionToRestore;
        setHistoryBusy(true);
        setHistoryError(null);
        try {
            const titlePage = target.title_page_snapshot ?? { ...titlePageData, title: target.title_snapshot ?? titlePageData.title };
            const snapshot = validateSnapshot({ blocks: target.script_content, titlePage,
                document: validateScriptDocument(target.document_snapshot) });
            // Preserve even unacknowledged edits before replacing editable state.
            await ProjectService.createVersion(projectId, currentSnapshot, 'Before restore — working draft');
            if (saveSession.current !== session) return;
            markEdit('Restore checkpoint');
            session?.restore(snapshot);
            applySnapshot(snapshot);
            setShowHistory(false);
            setShowRestoreConfirm(false);
            setVersionToRestore(null);
        } catch {
            if (saveSession.current === session) setHistoryError('The version could not be restored. Your current screenplay has been kept.');
        } finally {
            if (saveSession.current === session) setHistoryBusy(false);
        }
    };

    const confirmRestoreVersion = (version: ProjectVersion) => {
        setVersionToRestore(version);
        setShowRestoreConfirm(true);
    };

    const downloadDraft = (draft?: RecoveryDraft) => {
        const snapshot = draft?.snapshot ?? saveSession.current?.snapshot ?? currentSnapshot;
        const url = URL.createObjectURL(new Blob([JSON.stringify({ schema: 1, snapshot }, null, 2)], { type: 'application/json' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = 'ezart-story-recovery.json';
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    };

    const recoverDraft = (draft: RecoveryDraft) => {
        if (permissionReadOnly || !saveSession.current || loadedProjectId !== projectId) return;
        const snapshot = { ...draft.snapshot, document: validateScriptDocument(draft.snapshot.document) };
        markEdit('Restore local draft');
        saveSession.current.restore(snapshot, draft.id);
        applySnapshot(snapshot);
        setRecoveryDrafts([]);
    };

    const readFDXFile = async (file: File) => {
        const session = saveSession.current;
        const generation = ++importGeneration.current;
        setImportReview(null);
        setImportError(null);
        try {
            if (file.size > 5 * 1024 * 1024) throw new Error('Choose a Final Draft file smaller than 5 MB.');
            const bytes = await file.arrayBuffer();
            if (saveSession.current !== session || generation !== importGeneration.current) return;
            const xml = new TextDecoder('utf-8', { fatal: true, ignoreBOM: true }).decode(bytes);
            const declaredEncoding = xml.match(/<\?xml\b[^?]*\bencoding\s*=\s*['"]([^'"]+)['"]/i)?.[1];
            if (declaredEncoding && !/^utf-?8$/i.test(declaredEncoding)) throw new Error('This import supports UTF-8 Final Draft files.');
            const parsed = importFDXDocument(xml);
            if (parsed.diagnostics.length) throw new Error(parsed.diagnostics.map(item => item.message).join(' '));
            const projection = projectFDXToRichBlocks(parsed);
            const warningCodes = new Set(projection.diagnostics.map(item => item.code));
            const warnings: string[] = [];
            if (warningCodes.has('document-metadata-not-in-editor')) warnings.push('Document settings, the rich title page, and production metadata remain in the original file.');
            if (warningCodes.has('paragraph-metadata-not-in-editor')) warnings.push('Bold, italic, underline, and scene Number, Bookmark, and Label are editable. Other font and color attributes are preserved; imported notes and production metadata remain in the original file.');
            if (warningCodes.has('nested-run-xml-not-in-editor')) warnings.push('Nested text formatting is not editable.');
            projection.diagnostics.filter(d=>d.code==='revision-settings-not-in-editor').forEach(d=>warnings.push(d.message));
            validateScriptDocument({ schemaVersion: 1, blockNotes: {}, settings: documentData.settings,
                sourceFDX: { fileName: file.name, xml } });
            setImportReview({ fileName: file.name, xml, blocks: projection.blocks, warnings,revisions:projection.revisions });
        } catch (error) {
            if (saveSession.current === session && generation === importGeneration.current) setImportError(error instanceof Error ? error.message : 'The Final Draft file could not be read.');
        }
    };

    const confirmFDXImport = async () => {
        const session = saveSession.current;
        if (readOnly || !projectId || !session || session.conflicted || !importReview) return;
        setHistoryBusy(true);
        setImportError(null);
        try {
            const imported: ScriptSnapshot = { blocks: importReview.blocks, titlePage: titlePageData,
                document: { schemaVersion: 1, blockNotes: {}, settings: currentSnapshot.document!.settings,
                    revisions:importReview.revisions,
                    sourceFDX: { fileName: importReview.fileName, xml: importReview.xml, paragraphIds: importReview.blocks.map(block => block.id) } } };
            await ProjectService.createVersion(projectId, currentSnapshot, 'Before FDX import — working draft');
            if (saveSession.current !== session) return;
            markEdit('Import Final Draft screenplay');
            session.restore(imported);
            applySnapshot(imported);
            setImportOpen(false);
            setImportReview(null);
        } catch {
            if (saveSession.current === session) setImportError('The import could not be saved. Your current screenplay has been kept.');
        } finally {
            if (saveSession.current === session) setHistoryBusy(false);
        }
    };

    const [fdxExport, setFDXExport] = useState<{xml?: string; error?: string} | null>(null);
    const reviewFDXExport = () => {
        try { setFDXExport({xml: exportEditedFDX(blocks, documentData.sourceFDX,documentData.revisions)}); }
        catch (error) { setFDXExport({error: error instanceof Error ? error.message : 'FDX export failed.'}); }
    };
    const downloadEditedFDX = () => {
        if (!fdxExport?.xml) return;
        const url = URL.createObjectURL(new Blob([fdxExport.xml], {type: 'application/xml;charset=utf-8'}));
        const link = document.createElement('a'); link.href = url;
        link.download = `${(titlePageData.title || 'screenplay').replace(/[^a-z0-9_-]/gi, '_')}-edited.fdx`;
        link.click(); setTimeout(() => URL.revokeObjectURL(url), 1000); setFDXExport(null);
    };

    const downloadOriginalFDX = () => {
        if (!documentData.sourceFDX) return;
        const url = URL.createObjectURL(new Blob([documentData.sourceFDX.xml], { type: 'application/xml;charset=utf-8' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = documentData.sourceFDX.fileName.replace(/[\\/]/g, '_');
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    };

    // Scroll to block (for scene navigator)
    const scrollToBlock = (blockId: string) => {
        const element = containerRef.current?.querySelector(`[data-block-id="${blockId}"]`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setActiveId(blockId);
        }
    };

    // Open a scene from the Index Cards view
    const openScene = (blockId: string) => {
        setViewMode('screenplay');
        // The paper mounts on the next render; scroll once it is there
        setTimeout(() => scrollToBlock(blockId), 60);
    };

    // Handle note changes
    const handleNoteChange = (blockId: string, note: string) => {
        if (readOnly) return;
        markEdit('Edit block note', undefined, blockId);
        setBlockNotes(prev => {
            if (note) {
                return { ...prev, [blockId]: note };
            } else {
                const newNotes = { ...prev };
                delete newNotes[blockId];
                return newNotes;
            }
        });
    };

    const updateDualDialogue = (id:string, data:ScriptDualDialogue, paragraphId?:string, command=false) => {
        if (readOnly) return;
        markEdit(command ? 'Change dual dialogue' : 'Edit dual dialogue',command ? undefined : `dual:${id}:${paragraphId}`,id);
        setBlocks(previous => previous.map(block => block.id===id ? withDual(block,data) : block));
    };

    // Find & Replace handlers
    const handleReplaceInBlock = (blockId: string, oldText: string, newText: string, paragraphId?:string) => {
        if (readOnly) return;
        markEdit('Replace text', undefined, blockId);
        setBlocks(previous => previous.map(block => {
            if (block.id === blockId) {
                if (block.type==='dual-dialogue') return replaceDualText(block,oldText,newText,false,paragraphId,documentData.revisions?.mode?documentData.revisions.activeSet:undefined,documentData.revisions?.trackDeletions);
                const content=block.content.replace(oldText,newText),index=block.content.indexOf(oldText);
                return replaceBlockText(block,content,documentData.revisions?.mode?documentData.revisions.activeSet:undefined,documentData.revisions?.trackDeletions,observedTextEdit(block.content,content,index,index+oldText.length));
            }
            return block;
        }));
    };

    const handleReplaceAll = (oldText: string, newText: string) => {
        if (readOnly) return;
        markEdit('Replace all text');
        setBlocks(previous => previous.map(block => {
            if (!oldText) return block;
            if (block.type==='dual-dialogue') return replaceDualText(block,oldText,newText,true,undefined,documentData.revisions?.mode?documentData.revisions.activeSet:undefined,documentData.revisions?.trackDeletions);
            let result = block, offset = 0, index;
            while ((index = result.content.indexOf(oldText, offset)) >= 0) {
                const content=result.content.slice(0,index)+newText+result.content.slice(index+oldText.length);
                result = replaceBlockText(result,content,documentData.revisions?.mode?documentData.revisions.activeSet:undefined,documentData.revisions?.trackDeletions,observedTextEdit(result.content,content,index,index+oldText.length));
                offset = index + newText.length;
            }
            return result;
        }));
    };

    useEffect(() => {
        if (showHistory) {
            handleFetchVersions();
        }
    }, [showHistory, loadedProjectId]);

    return (
        <div className="flex h-full">
            {/* Scene Navigator Panel */}
            {showSceneNavigator && (
                <SceneNavigator
                    blocks={blocks}
                    onSceneClick={scrollToBlock}
                    onClose={() => setShowSceneNavigator(false)}
                    activeBlockId={activeId}
                />
            )}

            {/* Main Editor Area */}
            <div
                className="relative flex-1 min-w-0 min-h-screen pb-32 overflow-y-auto bg-neutral-800"
                ref={containerRef}
                onPointerDownCapture={() => undoHistory.current?.endGroup()}
                onBlurCapture={() => undoHistory.current?.endGroup()}
            >
                <ScriptEditingToolbar targetRef={setEditingToolbarTarget} readOnly={readOnly}/>


                <div className="mx-auto max-w-[1000px]">
                    <div className="flex items-center gap-3 px-6 py-3 border-b border-white/5">
                        <input
                            type="text"
                            readOnly={readOnly}
                            value={projectTitle}
                            onChange={(e) => {
                                markEdit('Edit title', 'title');
                                setProjectTitle(e.target.value);
                                setTitlePageData(prev => ({ ...prev, title: e.target.value }));
                            }}
                            placeholder="Untitled Screenplay"
                            aria-label="Screenplay title"
                            className="min-w-0 flex-1 text-sm font-medium bg-transparent border-none outline-none text-white placeholder:text-zinc-500"
                        />
                        <div className="flex-shrink-0 px-2.5 py-1 rounded-md bg-white/10 border border-white/10 text-[10px] font-bold uppercase tracking-widest text-zinc-400 select-none">
                            {projectType}
                        </div>
                        <Button variant="ghost" size="sm" aria-expanded={showDocumentDetails} onClick={()=>setShowDocumentDetails(value=>!value)}>Document details</Button>
                    </div>

                    {/* Title Page Properties Summary */}
                    {showDocumentDetails && <div className="px-6 py-4 border-b border-white/5">
                        <div
                            className="flex items-center gap-2 mb-2 text-zinc-500 text-xs uppercase tracking-wider font-medium select-none cursor-pointer hover:text-zinc-300 transition-colors group"
                            onClick={toggleProperties}
                        >
                            {showProperties ? <EyeIcon className="w-3.5 h-3.5" /> : <EyeClosedIcon className="w-3.5 h-3.5" />}
                            <span className="group-hover:text-zinc-300">Script Properties</span>
                            <div className="h-px flex-1 bg-zinc-800/50 ml-2" />
                        </div>

                        {showProperties && (
                            <div className="grid grid-cols-[120px_1fr] gap-y-0.5 gap-x-4 text-sm animate-in fade-in slide-in-from-top-1 duration-200">
                                <div className="text-zinc-500 py-1 flex items-center">Author</div>
                                <input
                                    value={titlePageData.author}
                                    readOnly={readOnly}
                                    onChange={(e) => {markEdit('Edit author', 'author'); setTitlePageData({ ...titlePageData, author: e.target.value });}}
                                    className="bg-transparent border-none outline-none text-zinc-300 placeholder:text-zinc-700 py-1 focus:bg-white/5 rounded px-2 -ml-2 transition-colors"
                                    placeholder="Add author..."
                                />

                                <div className="text-zinc-500 py-1 flex items-center">Contact</div>
                                <input
                                    value={titlePageData.contact || ''}
                                    readOnly={readOnly}
                                    onChange={(e) => {markEdit('Edit contact', 'contact'); setTitlePageData({ ...titlePageData, contact: e.target.value });}}
                                    className="bg-transparent border-none outline-none text-zinc-300 placeholder:text-zinc-700 py-1 focus:bg-white/5 rounded px-2 -ml-2 transition-colors"
                                    placeholder="Add contact info..."
                                />

                                <div className="text-zinc-500 py-1 flex items-center">Version</div>
                                <div className="text-zinc-400 py-1 px-2 -ml-2 flex items-center">Working draft</div>
                            </div>
                        )}
                    </div>}

                    {/* Removed Sticky Toolbar - Controls moved to StoryPage header */}
                    <div className="flex flex-wrap items-center gap-1 px-6 py-2">
                        <span className="mr-2 text-xs text-zinc-500">Draft</span>
                        <Button variant="ghost" size="sm" onClick={()=>setShowTitlePagePreview(true)}>Print Preview</Button>
                        <Button variant="ghost" size="sm" disabled={readOnly} onClick={openRevisionSettings}>Revisions{documentData.revisions?.mode?' · On':''}</Button>
                        <Button variant="ghost" size="sm" disabled={readOnly || !undoState.canUndo}
                            aria-label={undoState.undoLabel ? `Undo: ${undoState.undoLabel}` : 'Undo'} title="Undo (Ctrl/Cmd+Z)" onClick={() => travelHistory('undo')}>
                            <ArrowCounterClockwiseIcon className="mr-2 h-4 w-4" />Undo
                        </Button>
                        <Button variant="ghost" size="sm" disabled={readOnly || !undoState.canRedo}
                            aria-label={undoState.redoLabel ? `Redo: ${undoState.redoLabel}` : 'Redo'} title="Redo (Ctrl/Cmd+Shift+Z or Ctrl+Y)" onClick={() => travelHistory('redo')}>
                            <ArrowClockwiseIcon className="mr-2 h-4 w-4" />Redo
                        </Button>
                        <Button variant="ghost" size="sm" disabled={readOnly || !projectId || saveSession.current?.conflicted}
                            onClick={() => { setImportOpen(true); setImportReview(null); setImportError(null); }}>
                            <FileArrowUpIcon className="mr-2 h-4 w-4" />Import FDX
                        </Button>
                        <Button variant="ghost" size="sm" disabled={isLoading || !!loadError || loadedProjectId !== projectId || !blocks.length} onClick={reviewFDXExport}>
                            <FileArrowDownIcon className="mr-2 h-4 w-4" />Export edited FDX
                        </Button>
                        {documentData.sourceFDX && <Button variant="ghost" size="sm" onClick={downloadOriginalFDX}>
                            <FileArrowDownIcon className="mr-2 h-4 w-4" />Download original FDX
                        </Button>}
                    </div>
                    {showDocumentDetails && documentData.sourceFDX && <p className="px-6 py-3 text-xs text-zinc-400">
                        Original file preserved: {documentData.sourceFDX.fileName}. Edits here change the screenplay copy; the original file stays unchanged.
                    </p>}

                    {saveError && <div role="alert" className="mb-4 rounded border border-red-500/30 p-3 text-sm text-red-300">
                        <p>{saveSession.current?.conflicted ? saveError : 'Your changes have not been saved. Keep this page open and try again.'}</p>
                        <Button variant="ghost" size="sm" onClick={() => downloadDraft()}>Download draft</Button>
                        {saveSession.current?.conflicted
                            ? <Button variant="ghost" size="sm" onClick={() => setLoadAttempt(attempt => attempt + 1)}>Reload cloud version</Button>
                            : <Button variant="ghost" size="sm" disabled={readOnly} onClick={() => saveSession.current?.retry()}>Retry save</Button>}
                    </div>}
                    {recoveryError && <div role="alert" className="mb-4 rounded border border-amber-500/30 p-3 text-sm text-amber-200">
                        <p>{recoveryError}</p><Button variant="ghost" size="sm" onClick={() => downloadDraft()}>Download draft</Button>
                    </div>}
                    {recoveryDrafts.length > 0 && <div role="alert" className="mb-4 rounded border border-amber-500/30 p-3 text-sm text-amber-200">
                        <p>A local draft is available. Restoring it saves a cloud checkpoint before replacing the screenplay.</p>
                        {recoveryDrafts.map(draft => <div key={draft.id} className="mt-2">
                            <p>{new Date(draft.savedAt).toLocaleString()}{draft.baseRevision !== cloudRevision ? ' — cloud version has changed' : ''}</p>
                            <Button variant="ghost" size="sm" disabled={permissionReadOnly} onClick={() => recoverDraft(draft)}>Restore local draft</Button>
                            <Button variant="ghost" size="sm" onClick={() => downloadDraft(draft)}>Download local draft</Button>
                        </div>)}
                        <Button variant="ghost" size="sm" onClick={() => setRecoveryDrafts([])}>Use cloud version; keep local drafts</Button>
                    </div>}
                    {historyError && <div role="alert" className="mb-4 rounded border border-red-500/30 p-3 text-sm text-red-300">{historyError}</div>}
                    {loadError ? (
                        <div role="alert" className="py-12 text-sm text-red-300">
                            <p>{loadError}</p>
                            <Button variant="ghost" size="sm" onClick={() => setLoadAttempt(attempt => attempt + 1)}>Retry loading</Button>
                        </div>
                    ) : isLoading || loadedProjectId !== projectId ? (
                        <div className="flex flex-col items-center justify-center gap-4 py-20 text-zinc-500 animate-pulse">
                            <div className="w-8 h-8 border-2 border-zinc-800 border-t-zinc-400 rounded-full animate-spin" />
                            <p className="text-xs tracking-widest uppercase">Loading Script...</p>
                        </div>
                    ) : viewMode === 'cards' ? (
                        <SceneCards
                            blocks={blocks}
                            startPages={startPages}
                            sceneNumbers={sceneNumberMap}
                            onOpenScene={openScene}
                        />
                    ) : (
                        <ScriptDraftPaper format={scriptFormat} layout={printLayout}><div className="relative w-full">
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragStart={handleDragStart}
                                onDragEnd={handleDragEnd}
                            >
                                <SortableContext
                                    items={blocks.map(b => b.id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    <div data-story-blocks className="flex flex-col">
                                        {blocks.map((block) => (
                                            <ScriptBlock
                                                format={scriptFormat}
                                                key={block.id}
                                                block={block}
                                                isActive={activeId === block.id}
                                                caretRequest={activeId === block.id ? caretRequest : undefined}
                                                toolbarTarget={editingToolbarTarget}
                                                suggestions={suggestions}
                                                onUpdate={updateBlock}
                                                onFormat={formatBlock}
                                                onParagraphAttributes={updateParagraphAttributes}
                                                onDualChange={updateDualDialogue}
                                                onAlternateChange={updateAlternate}
                                                revisions={documentData.revisions}
                                                revisionRanges={revisionRanges.get(block.id)}
                                                onRevisionMark={updateRevisionMark}
                                                onTypeChange={updateType}
                                                onEnter={addBlock}
                                                onDelete={deleteBlock}
                                                onBackspace={handleBackspace}
                                                onDuplicate={(id) => insertBlock(id, { kind: 'duplicate' })}
                                                onTab={handleTab}
                                                onFocus={(id) => { if (id !== activeId) {setCaretRequest(undefined); setActiveId(id);} }}
                                                onMoveUp={(id) => moveSelection(id, 'up')}
                                                onMoveDown={(id) => moveSelection(id, 'down')}
                                                isSelected={selectedBlockIds.has(block.id)}
                                                isMultiSelectMode={isMultiSelectMode}
                                                onToggleSelection={toggleBlockSelection}
                                                onEnterMultiSelectMode={() => setIsMultiSelectMode(true)}
                                                sceneNumber={sceneNumberMap[block.id]}
                                                showSceneNumbers={showSceneNumbers}
                                                note={blockNotes[block.id]}
                                                onNoteChange={handleNoteChange}
                                                readOnly={readOnly}
                                            />
                                        ))}
                                    </div>
                                </SortableContext>

                                <DragOverlay>
                                    {dragActiveId ? (() => {
                                        const block = blocks.find(b => b.id === dragActiveId);
                                        if (!block) return null;

                                        const count = selectedBlockIds.has(dragActiveId) ? selectedBlockIds.size : 1;

                                        return (
                                            <div className="opacity-90 pointer-events-none origin-left">
                                                <div className="relative">
                                                    <ScriptBlock
                                                        format={scriptFormat}
                                                        block={block}
                                                        isActive={false}
                                                        onUpdate={() => { }}
                                                        onTypeChange={() => { }}
                                                        onEnter={() => { }}
                                                        onDelete={() => { }}
                                                        onBackspace={() => { }}
                                                        onDuplicate={() => { }}
                                                        onTab={() => { }}
                                                        onFocus={() => { }}
                                                        onMoveUp={() => { }}
                                                        onMoveDown={() => { }}
                                                        isOverlay={true}
                                                    />
                                                    {/* Multi-drag badge */}
                                                    {count > 1 && (
                                                        <div className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center shadow-lg border-2 border-zinc-900">
                                                            {count}
                                                        </div>
                                                    )}
                                                </div>
                                                {count > 1 && (
                                                    <div className="absolute top-2 left-2 w-full h-full bg-graphite border border-white/10 rounded shadow-xl -z-10 translate-x-1 translate-y-1" />
                                                )}
                                            </div>
                                        );
                                    })() : null}
                                </DragOverlay>
                            </DndContext>

                            {/* Bottom Add Area */}
                            <div
                                className="opacity-0 hover:opacity-100 transition-opacity cursor-pointer flex justify-center py-8"
                                onClick={() => blocks.length > 0 && addBlock(blocks[blocks.length - 1].id, 'action')}
                            >
                                <div className="flex items-center gap-2 text-zinc-600 hover:text-zinc-400">
                                    <PlusIcon className="w-4 h-4" />
                                    <span className="text-sm">Click to add block at end</span>
                                </div>
                            </div>
                        </div></ScriptDraftPaper>
                    )}
                </div>

                {/* Floating Status Bar */}
                <div className="fixed bottom-6 z-[50] flex justify-center pointer-events-none" style={{ left: 'var(--sidebar-left, 0px)', right: 'var(--sidebar-right, 0px)' }}>
                <div className="pointer-events-auto bg-zinc-900/95 border border-white/10 rounded-full px-4 py-2 flex items-center gap-4 shadow-xl backdrop-blur-md">
                    {/* Page Count */}
                    <div className="flex items-center gap-2 text-xs">
                        <FileArrowDownIcon className="w-3.5 h-3.5 text-zinc-500" />
                        <span className="text-zinc-400">{pageCount}</span>
                        <span className="text-zinc-600">page{pageCount !== 1 ? 's' : ''}</span>
                    </div>

                    <div className="h-3 w-px bg-white/10" />

                    {/* Scene Count */}
                    <div className="flex items-center gap-2 text-xs">
                        <FilmSlateIcon className="w-3.5 h-3.5 text-zinc-500" />
                        <span className="text-zinc-400">{sceneCount}</span>
                        <span className="text-zinc-600">scene{sceneCount !== 1 ? 's' : ''}</span>
                    </div>

                    <div className="h-3 w-px bg-white/10" />

                    {/* Est. Runtime */}
                    <div className="flex items-center gap-2 text-xs">
                        <ClockIcon className="w-3.5 h-3.5 text-emerald-500" />
                        <span className="text-emerald-400">~{pageCount} min</span>
                    </div>

                    <div className="h-3 w-px bg-white/10" />

                    {/* Quick Actions */}
                    <button
                        onClick={() => setShowFindReplace(prev => !prev)}
                        className={`p-1 rounded transition-colors ${showFindReplace ? 'text-blue-400 bg-blue-500/20' : 'text-zinc-500 hover:text-zinc-300'}`}
                        title="Find & Replace (Ctrl+F)"
                    >
                        <MagnifyingGlassIcon className="w-4 h-4" />
                    </button>
                    <button
                        onClick={toggleSceneNumbers}
                        aria-pressed={showSceneNumbers}
                        className={`p-1 rounded transition-colors ${showSceneNumbers ? 'text-amber-400 bg-amber-500/20' : 'text-zinc-500 hover:text-zinc-300'}`}
                        title="Scene Numbers"
                    >
                        <HashIcon className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => setShowSceneNavigator(prev => !prev)}
                        className={`p-1 rounded transition-colors ${showSceneNavigator ? 'text-indigo-400 bg-indigo-500/20' : 'text-zinc-500 hover:text-zinc-300'}`}
                        title="Scene Navigator"
                    >
                        <ListBulletsIcon className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => setShowStats(prev => !prev)}
                        className={`p-1 rounded transition-colors ${showStats ? 'text-emerald-400 bg-emerald-500/20' : 'text-zinc-500 hover:text-zinc-300'}`}
                        title="Script Stats"
                    >
                        <ChartBarIcon className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => setShowTitlePagePreview(true)}
                        className="p-1 rounded transition-colors text-zinc-500 hover:text-zinc-300"
                        title="Print Preview"
                    >
                        <PrinterIcon className="w-4 h-4" />
                    </button>
                </div>
                </div>

                {/* Version History Modal */}
                <Modal
                    isOpen={showHistory}
                    onClose={() => setShowHistory(false)}
                    title="Version History"
                    description="Restore previous snapshots of your script"
                    size="lg"
                >
                    <div className="p-2">
                        <div className="flex justify-end mb-2 px-2">
                            <Button size="sm" variant="secondary" disabled={readOnly} onClick={() => setShowVersionModal(true)} className="h-7 text-xs gap-1.5">
                                <FloppyDiskIcon className="w-3.5 h-3.5" />
                                Save Snapshot
                            </Button>
                        </div>
                        {historyError && <div role="alert" className="p-3 text-sm text-red-300">
                            {historyError}<Button variant="ghost" size="sm" onClick={() => void handleFetchVersions()}>Retry history</Button>
                        </div>}
                        <div className="space-y-1 max-h-[50vh] overflow-y-auto">
                            {loadingVersions ? (
                                <div className="p-4 text-center text-zinc-500 text-sm">Loading history...</div>
                            ) : versions.length === 0 ? (
                                <div className="p-8 text-center text-zinc-600 text-sm italic">
                                    No saved versions yet.
                                    <br />
                                    Preserve your progress by saving a snapshot.
                                </div>
                            ) : (
                                versions.map(v => (
                                    <div key={v.id} className="group flex items-center justify-between p-3 rounded-lg hover:bg-white/5 border border-transparent hover:border-white/5 transition-all">
                                        <div className="flex flex-col">
                                            <span className="text-sm font-medium text-zinc-300">{v.label}</span>
                                            <span className="text-[10px] text-zinc-500">
                                                {new Date(v.created_at).toLocaleString()} • Ver {v.version_number}
                                            </span>
                                        </div>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => confirmRestoreVersion(v)}
                                            disabled={readOnly || !!saveSession.current?.conflicted}
                                            className="h-7 px-3 text-xs opacity-0 group-hover:opacity-100 transition-opacity bg-white/5 hover:bg-white/10 hover:text-white"
                                        >
                                            Restore
                                        </Button>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                    <div className="p-3 border-t border-white/5 bg-zinc-900/50 text-center">
                        <span className="text-[10px] text-zinc-600">Auto-saving enabled. Snapshots are manual checkpoints.</span>
                    </div>
                </Modal>

                {/* Version Name Input Modal */}
                <InputModal
                    isOpen={showVersionModal}
                    onClose={() => setShowVersionModal(false)}
                    onConfirm={handleCreateVersion}
                    title="Save Version"
                    description="Give this snapshot a name to identify it later"
                    placeholder="e.g., Draft 2 - Added climax scene"
                    defaultValue={`Draft ${new Date().toLocaleTimeString()}`}
                    confirmText="Save"
                />

                {/* Restore Confirm Modal */}
                <ConfirmModal
                    isOpen={showRestoreConfirm}
                    onClose={() => { setShowRestoreConfirm(false); setVersionToRestore(null); }}
                    onConfirm={handleRestoreVersion}
                    title="Restore Version"
                    description={`Restore "${versionToRestore?.label}"? Your working draft and the cloud document will be preserved in history before replacement.`}
                    confirmText="Restore"
                    variant="danger"
                />

                {/* Multi-select Floating Bar */}
                {isMultiSelectMode && (
                    <div className="fixed bottom-20 z-[100] flex justify-center pointer-events-none" style={{ left: 'var(--sidebar-left, 0px)', right: 'var(--sidebar-right, 0px)' }}>
                    <div className="pointer-events-auto bg-zinc-900 border border-white/10 rounded-full px-6 py-3 flex items-center gap-6 shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-300 backdrop-blur-md">
                        <div className="flex items-center gap-3">
                            <div className="w-6 h-6 bg-indigo-500 rounded flex items-center justify-center shadow-lg shadow-indigo-500/20">
                                <CheckSquareIcon className="w-4 h-4 text-white" />
                            </div>
                            <span className="text-sm font-bold text-white tracking-tight">{selectedBlockIds.size} {selectedBlockIds.size === 1 ? 'Block' : 'Blocks'} Selected</span>
                        </div>
                        <div className="h-4 w-px bg-white/10" />
                        <button
                            onClick={exitMultiSelectMode}
                            className="text-xs font-bold uppercase tracking-widest text-zinc-400 hover:text-white transition-colors"
                        >
                            Exit
                        </button>
                        <button
                            onClick={() => {
                                if (readOnly) return;
                                markEdit('Delete screenplay blocks');
                                setBlocks(previous => removeRevisionBlocks(previous,selectedBlockIds,documentData.revisions));
                                exitMultiSelectMode();
                            }}
                            className="text-xs font-bold uppercase tracking-widest text-red-400/80 hover:text-red-400 transition-colors"
                        >
                            Delete
                        </button>
                    </div>
                    </div>
                )}
            </div>

            {/* Stats Panel */}
            {showStats && (
                <ScriptStats
                    blocks={blocks}
                    onClose={() => setShowStats(false)}
                />
            )}

            {/* Find & Replace Panel */}
            <FindReplace
                isOpen={showFindReplace}
                onClose={() => setShowFindReplace(false)}
                blocks={blocks}
                onNavigateToBlock={scrollToBlock}
                onReplaceInBlock={handleReplaceInBlock}
                onReplaceAll={handleReplaceAll}
            />

            {/* Title Page Preview */}
            <TitlePagePreview
                revisions={documentData.revisions}
                isOpen={showTitlePagePreview}
                onClose={() => setShowTitlePagePreview(false)}
                titlePageData={titlePageData}
                layout={printLayout}
                blocks={blocks}
                onExportPDF={handleExportPDF}
            />
            <Modal isOpen={Boolean(fdxExport)} onClose={() => setFDXExport(null)} title="Export edited FDX">
                <div className="space-y-4 text-sm text-zinc-300">
                    {fdxExport?.error ? <p role="alert">{fdxExport.error}</p> : <>
                        <p>The export contains the current screenplay text, supported text styles, and scene identifiers.</p>
                        <p>{documentData.sourceFDX ? 'The original title page and document settings are retained. Story’s title fields and block notes are not applied to the imported FDX.' : 'This export contains the screenplay body. Story’s title fields and block notes are not included.'}</p>
                        <Button onClick={downloadEditedFDX}>Download edited FDX</Button>
                    </>}
                </div>
            </Modal>
            <Modal isOpen={Boolean(exportError)} onClose={() => setExportError(null)} title="Screenplay">
                <p role="alert" className="p-4 text-sm text-zinc-300">{exportError}</p>
            </Modal>
            <Modal isOpen={showRevisions} onClose={()=>setShowRevisions(false)} title="Revisions">
                {documentData.revisions&&<div className="p-4"><RevisionSettings value={documentData.revisions} blocks={blocks} readOnly={readOnly} onClearDeletions={()=>{if(readOnly)return;markEdit('Clear deletion marks');setBlocks(previous=>clearDeletionMarks(previous,documentData.revisions!.activeSet));}} onChange={revisions=>{
                    if(readOnly)return;validateRevisionReferences(blocks,revisions);markEdit('Change revision settings');setDocumentData(previous=>({...previous,revisions}));
                }}/></div>}
            </Modal>
            <Modal isOpen={importOpen} onClose={() => { if (!historyBusy) { importGeneration.current++; setImportOpen(false); } }} title="Import Final Draft screenplay"
                description="Review the editable copy before replacing your screenplay. Your working draft and cloud screenplay will be preserved in history.">
                <div className="space-y-4 p-4 text-sm text-zinc-300">
                    <label className="block">Choose a UTF-8 Final Draft file (up to 5 MB)
                        <input aria-label="Final Draft file" type="file" accept=".fdx,.fdxt" disabled={historyBusy}
                            className="mt-2 block w-full text-sm" onChange={event => {
                                const file = event.currentTarget.files?.[0];
                                if (file) void readFDXFile(file);
                                event.currentTarget.value = '';
                            }} />
                    </label>
                    {importError && <p role="alert" className="text-red-300">{importError}</p>}
                    {importReview && <>
                        <p>{importReview.fileName} — {importReview.blocks.length} screenplay blocks.</p>
                        <p>The original FDX will be saved with this project and its checkpoints. The editable copy uses Story's supported text elements and current title-page fields.</p>
                        {importReview.warnings.length > 0 && <ul className="list-disc space-y-1 pl-5 text-amber-200">
                            {importReview.warnings.map(warning => <li key={warning}>{warning}</li>)}
                        </ul>}
                        <Button disabled={readOnly || saveSession.current?.conflicted} onClick={() => void confirmFDXImport()}>Import screenplay copy</Button>
                    </>}
                </div>
            </Modal>
        </div>
    );
});
ScriptEditor.displayName = 'ScriptEditor';
